/**
 * Reserve privacy indicator, v1: a pure, local estimate per asset, following
 * "Redacted Reserve Privacy Indicator Exploration" (XXXXXX, 2026-09-15).
 *
 * Every input is data this browser already holds or reads identically for
 * every user: the user's own notes and public pool statistics from the account
 * scan, the proxy's Reserve total for every denom it holds (read around that
 * scan), and the balances of the user's own public subs. Results are never
 * logged, stored or sent.
 *
 * Rules. Any of them makes an asset "limited"; none can make it better:
 * - only_holder: nobody else holds the asset in the Reserve.
 * - largest_holder: the user's amount (Reserve plus funds on their way back
 *   from Spending) is larger than everyone else's combined, so a move above
 *   the others' total can only come from this user.
 * - small_pool: fewer than `smallPoolThreshold` other unspent notes exist.
 * - unique_note_size: a note's encrypted size matches no other reserve record
 *   (the size leaks the amount's digit count). This is per note: when only some
 *   of an asset's notes are affected, the asset is limited and "mixed".
 * - carried_over: a note the user's own merge, change or return made from
 *   funds that were limited at the time keeps that limit, whatever its size
 *   (the session records these in memory; see `inheritsLimit`).
 * "partly_public" (funds in one of the user's public subs) is a flag shown
 * next to the level, never averaged into it. Dust next to a Reserve balance is
 * ignored. An asset with nothing in the Reserve ("spending_only": a swap
 * output waiting for the claw-back, a position token, dust left behind) has
 * nothing private to assess: it is "limited", and public however small.
 * Totals that could not be read, or that changed while the scan ran, make the
 * asset "unknown".
 *
 * No rule reads a clock, a block height or a note's age, so waiting never
 * improves a label. Returning funds and moving one's own notes cannot improve
 * one either: returning funds count as the user's while they are on their way
 * back (in Spending or sent but not yet scanned), and a note made from limited
 * funds carries the limit over.
 */
import type { V2Coin } from "../action";
import { isPositionToken } from "../rotation";
import type {
  V2PrivacyAssessment,
  V2PrivacyLevel,
  V2PrivacyReason,
} from "./types";

/** Fewer other unspent notes than this in the whole pool: every asset is limited. */
export const V2_PRIVACY_SMALL_POOL = 20;

/** One of the user's own reserve notes from the account scan. */
export type V2PrivacyNote = {
  denom: string;
  amount: bigint;
  /** Length in bytes of the note's encrypted payload on chain. */
  encryptedLength?: number;
  /** Spent notes only serve to exclude the user's own records from the size comparison. */
  spent: boolean;
  /** The note's commitment: its identity within this session, never shown. */
  commitment?: string;
  /**
   * Made by the user's own merge, change or return from funds that were
   * limited at the time: it stays limited whatever its size.
   */
  inheritsLimit?: boolean;
};

/** Public statistics of the whole pool, counted during the same scan. */
export type V2PrivacyPool = {
  /** Notes ever inserted (the scanned tree's next_index). */
  nextIndex: number;
  /** Notes ever spent (the scanned tree's next_nullifier_index). */
  nextNullifierIndex: number;
  /** Encrypted length in bytes → reserve records of that length (the user's own included). */
  reserveLengths: ReadonlyMap<number, number>;
};

/** One read of the proxy's Reserve totals, for every denom it holds. */
export type V2PrivacyTotals = {
  /**
   * Liability per denom; undefined where that read failed. A denom the proxy
   * does not hold is absent: nobody holds it in the Reserve (0).
   */
  liabilities: ReadonlyMap<string, bigint | undefined>;
};

export type V2PrivacyInput = {
  /** The user's own reserve notes from the scan, spent and unspent. */
  notes: readonly V2PrivacyNote[];
  /** The user's unspent notes of every kind (reserve and control). */
  activeNoteCount: number;
  /** Undefined when the scan provided no pool statistics. */
  pool?: V2PrivacyPool;
  /** Pool totals read before the scan started. */
  totalsBefore?: V2PrivacyTotals;
  /** Pool totals read after the scan finished. */
  totalsAfter?: V2PrivacyTotals;
  /**
   * A tree read taken after `totalsAfter` still matched the scanned tree: no
   * note entered or left the pool since the scan, so `totalsAfter` describes
   * exactly the scanned state.
   */
  unchangedSinceScan?: boolean;
  /**
   * The pool moved since the scan, but none of the user's own Reserve notes
   * did: `totalsAfter` minus the scanned notes is exactly what others hold at
   * that read. Others' moves only make the totals newer, never wrong.
   */
  ownUnchangedSinceScan?: boolean;
  /** Every coin held by the user's own public subs (one entry per sub and denom). */
  spending: readonly Readonly<V2Coin>[];
  /**
   * Returns to the Reserve already sent (their coin has left Spending) that
   * the scan does not show yet: still the user's funds on their way back.
   */
  returned?: readonly { denom: string; amount: bigint }[];
  isDust: (coin: Readonly<V2Coin>) => boolean;
  smallPoolThreshold?: number;
};

/** Per-asset labels, and which of the user's notes are limited on their own. */
export type V2PrivacyResult = {
  byDenom: Map<string, V2PrivacyAssessment>;
  /**
   * Commitments of held notes that are limited (by an asset-wide rule, their
   * size or a carried-over limit): a note made from one of them inherits it.
   */
  limitedNotes: Set<string>;
};

const REASON_ORDER: readonly V2PrivacyReason[] = [
  "data_unavailable",
  "spending_only",
  "only_holder",
  "largest_holder",
  "small_pool",
  "unique_note_size",
  "carried_over",
  "partly_public",
];

const assessment = (
  denom: string,
  level: V2PrivacyLevel,
  reasons: ReadonlySet<V2PrivacyReason>,
  partlyPublic: boolean,
  mixed = false
): V2PrivacyAssessment => ({
  denom,
  level,
  mixed,
  partlyPublic,
  // Account-link detection needs the registration replay (not in v1).
  linkDetected: false,
  reasons: REASON_ORDER.filter((reason) => reasons.has(reason)),
});

const sumByDenom = (
  entries: Iterable<{ denom: string; amount: bigint }>
): Map<string, bigint> => {
  const sums = new Map<string, bigint>();
  for (const { denom, amount } of entries)
    sums.set(denom, (sums.get(denom) ?? 0n) + amount);
  return sums;
};

const units = (amount: string): bigint =>
  /^\d{1,78}$/.test(amount) ? BigInt(amount) : 0n;

const safeIsDust = (
  isDust: V2PrivacyInput["isDust"],
  coin: Readonly<V2Coin>
): boolean => {
  try {
    return isDust(coin);
  } catch {
    // An unreadable dust rule never hides a public balance.
    return false;
  }
};

/**
 * The user's public balances per denom, and which denoms are (partly) public:
 * dust next to a Reserve balance is ignored, but a denom with nothing in the
 * Reserve is public however small its balance. `returning` is what the
 * claw-back brings back to the Reserve (neither dust nor a position token).
 */
const publicBalances = (
  spending: V2PrivacyInput["spending"],
  returned: NonNullable<V2PrivacyInput["returned"]>,
  isDust: V2PrivacyInput["isDust"],
  reserve: ReadonlyMap<string, bigint>
) => {
  const coins = spending.flatMap((coin) => {
    const amount = units(coin.amount);
    return amount > 0n
      ? [{ denom: coin.denom, amount, dust: safeIsDust(isDust, coin) }]
      : [];
  });
  const kept = coins.filter((coin) => !coin.dust);
  const visible = new Set(kept.map((coin) => coin.denom));
  const sent = returned.filter(({ amount }) => amount > 0n);
  return {
    shown: new Set([...coins, ...sent].map((coin) => coin.denom)),
    isPublic: (denom: string) =>
      visible.has(denom) ||
      (!reserve.has(denom) && coins.some((coin) => coin.denom === denom)),
    // Position tokens stay in their sub; everything else is clawed back.
    returning: sumByDenom([
      ...kept.filter((coin) => !isPositionToken(coin.denom)),
      ...sent,
    ]),
  };
};

const heldReserve = (notes: readonly V2PrivacyNote[]) => {
  const held = notes.filter((note) => !note.spent && note.amount > 0n);
  return { held, reserve: sumByDenom(held) };
};

const liabilityIn = (
  totals: V2PrivacyTotals | undefined,
  denom: string
): bigint | undefined => {
  if (!totals) return undefined;
  return totals.liabilities.has(denom) ? totals.liabilities.get(denom) : 0n;
};

/**
 * The Reserve total that matches the scanned notes, if any. The read after the
 * scan counts when the pool did not move since the scan, when only others
 * moved (the user's own notes are still the scanned ones), or when the read
 * before the scan shows the same total (it did not change while it ran).
 */
const scannedLiability = (
  input: V2PrivacyInput,
  denom: string
): bigint | undefined => {
  const after = liabilityIn(input.totalsAfter, denom);
  if (after === undefined) return undefined;
  if (input.unchangedSinceScan || input.ownUnchangedSinceScan) return after;
  return liabilityIn(input.totalsBefore, denom) === after ? after : undefined;
};

/** Every held asset unknown (for a failed assessment); public parts stay flagged. */
export const unavailableReservePrivacy = (
  input: Pick<V2PrivacyInput, "notes" | "spending" | "isDust">
): Map<string, V2PrivacyAssessment> => {
  const { reserve } = heldReserve(input.notes);
  const { shown, isPublic } = publicBalances(
    input.spending,
    [],
    input.isDust,
    reserve
  );
  const result = new Map<string, V2PrivacyAssessment>();
  for (const denom of [...new Set([...reserve.keys(), ...shown])].sort()) {
    const partlyPublic = isPublic(denom);
    const reasons = new Set<V2PrivacyReason>(["data_unavailable"]);
    if (partlyPublic) reasons.add("partly_public");
    result.set(denom, assessment(denom, "unknown", reasons, partlyPublic));
  }
  return result;
};

/**
 * Per-asset labels for every denom the user holds in the Reserve, in one of
 * their public subs (dust there only counts when the Reserve holds none) or
 * on its way back to the Reserve, and the user's notes that are limited.
 */
export const assessReservePrivacyDetailed = (
  input: V2PrivacyInput
): V2PrivacyResult => {
  const threshold = input.smallPoolThreshold ?? V2_PRIVACY_SMALL_POOL;
  const { held, reserve } = heldReserve(input.notes);
  const { shown, isPublic, returning } = publicBalances(
    input.spending,
    input.returned ?? [],
    input.isDust,
    reserve
  );

  // The user's own records (spent ones too) are never cover for their notes.
  const ownLengths = new Map<number, number>();
  let lengthsKnown = true;
  for (const note of input.notes) {
    if (note.encryptedLength === undefined) lengthsKnown = false;
    else
      ownLengths.set(
        note.encryptedLength,
        (ownLengths.get(note.encryptedLength) ?? 0) + 1
      );
  }
  const pool = input.pool;
  const otherUnspent = pool
    ? pool.nextIndex - pool.nextNullifierIndex - input.activeNoteCount
    : -1;
  // A scan that does not account for the user's own records is not usable.
  const poolUsable =
    !!pool &&
    lengthsKnown &&
    otherUnspent >= 0 &&
    [...ownLengths].every(
      ([length, count]) => (pool.reserveLengths.get(length) ?? 0) >= count
    );
  const sharesSize = (length: number): boolean =>
    (pool?.reserveLengths.get(length) ?? 0) - (ownLengths.get(length) ?? 0) > 0;

  const byDenom = new Map<string, V2PrivacyAssessment>();
  const limitedNotes = new Set<string>();
  const markLimited = (notes: readonly V2PrivacyNote[]) => {
    for (const note of notes)
      if (note.commitment !== undefined) limitedNotes.add(note.commitment);
  };
  for (const denom of [...new Set([...reserve.keys(), ...shown])].sort()) {
    const partlyPublic = isPublic(denom);
    const reasons = new Set<V2PrivacyReason>();
    if (partlyPublic) reasons.add("partly_public");
    const own = reserve.get(denom) ?? 0n;
    const back = returning.get(denom) ?? 0n;
    const notes = held.filter((note) => note.denom === denom);
    const carried = notes.filter((note) => note.inheritsLimit);
    if (carried.length) reasons.add("carried_over");
    if (own === 0n) {
      // Nothing of it is in the Reserve: a Spending balance (a swap output
      // waiting for the claw-back, a position token, dust left behind) or a
      // return no scan shows yet. Nothing of it is private, whatever the
      // Reserve holds for others.
      reasons.add("spending_only");
      byDenom.set(denom, assessment(denom, "limited", reasons, partlyPublic));
      continue;
    }
    const liability = scannedLiability(input, denom);
    // More of the user's own funds than the whole total: inconsistent reads.
    const others = liability === undefined ? -1n : liability - own;
    if (!poolUsable || others < 0n) {
      reasons.add("data_unavailable");
      // A limit carried over from the user's own funds stays known.
      markLimited(carried);
      byDenom.set(
        denom,
        assessment(
          denom,
          carried.length ? "limited" : "unknown",
          reasons,
          partlyPublic
        )
      );
      continue;
    }
    if (others === 0n) reasons.add("only_holder");
    else if (own + back > others) reasons.add("largest_holder");
    if (otherUnspent < threshold) reasons.add("small_pool");
    const unique = notes.filter(
      (note) => !sharesSize(note.encryptedLength as number)
    );
    if (unique.length) reasons.add("unique_note_size");
    // Notes limited on their own: a unique size or a carried-over limit.
    const standOut = notes.filter(
      (note) => note.inheritsLimit || unique.includes(note)
    );
    const assetLimited =
      reasons.has("only_holder") ||
      reasons.has("largest_holder") ||
      reasons.has("small_pool");
    markLimited(assetLimited ? notes : standOut);
    byDenom.set(
      denom,
      assessment(
        denom,
        assetLimited || standOut.length ? "limited" : "no_known_link",
        reasons,
        partlyPublic,
        // Some notes are limited on their own and others are not.
        !assetLimited && standOut.length > 0 && standOut.length < notes.length
      )
    );
  }
  return { byDenom, limitedNotes };
};

/** Per-asset labels (see `assessReservePrivacyDetailed`). */
export const assessReservePrivacy = (
  input: V2PrivacyInput
): Map<string, V2PrivacyAssessment> =>
  assessReservePrivacyDetailed(input).byDenom;
